// Colour tier per access level, following the *-status-config.tsx pattern used by
// orders / events / products. A config map rather than StatusBadge, which mandates
// an icon and a border that this tag doesn't have.

import type { User } from '@api/users';

interface AccessLevelTier {
  bgClassName: string;
  textClassName: string;
}

// 12px SemiBold, matching every other in-row tag in the app.
export const ACCESS_LEVEL_TAG_CLASSES =
  'inline-flex items-center rounded-xs px-2 py-0.5 font-inter text-xs font-semibold leading-[1.25] whitespace-nowrap';

const YELLOW: AccessLevelTier = {
  bgClassName: 'bg-custard-yellow',
  textClassName: 'text-turmeric-yellow',
};

const GREEN: AccessLevelTier = {
  bgClassName: 'bg-yield-positive-bg',
  textClassName: 'text-confirmed-green',
};

const GREY: AccessLevelTier = {
  bgClassName: 'bg-dinnerplate-white',
  textClassName: 'text-grey',
};

// Exhaustive over the roles the frontend knows, so a role added to the union
// has to be given a colour here.
export const ACCESS_LEVEL_TIERS: Record<User['role'], AccessLevelTier> = {
  admin: YELLOW,
  // Developer is an admin superset, so it shares admin's top-tier colour.
  developer: YELLOW,
  sales_admin: GREEN,
  fulfilment_admin: GREEN,
  fulfilment_executive: GREY,
  coordinator: GREY,
  dispatcher: GREY,
  salesperson: GREY,
  sales_coordinator: GREY,
  member: GREY,
  rnd: GREY,
  // Storytelling extends the member set with product write; still grey tier.
  storytelling: GREY,
  specialist: GREY,
  catering_assistant: GREY,
  packer: GREY,
  warehouse_assistant: GREY,
  inhouse_driver: GREY,
  finance: GREY,
  production: GREY,
  // Not a human access level, but User.system is verified and unfiltered by role,
  // so it shows up in the Active tab.
  system: GREY,
};

// The union above is a hand-written copy of `User::ROLES`, so the server can
// send a role this map has never heard of. Reading a colour straight off the
// lookup threw inside a cell renderer and took the whole table down; an unknown
// role now reads as an untiered tag instead.
export function accessLevelTier(role: string): AccessLevelTier {
  return (ACCESS_LEVEL_TIERS as Record<string, AccessLevelTier>)[role] ?? GREY;
}
